/**
 * Initialize analytics tracking
 * Only runs in production environment
 */
export const initAnalytics = () => {
  if (typeof window === 'undefined') return;

  // Initialize analytics using existing configuration
  if (process.env.NODE_ENV === 'production') {
    // console.log('Analytics initialized in production mode');
  } else {
    // console.log('Analytics initialized in debug mode');
  }
};

/**
 * Track key performance metrics
 * Measures page load time, first paint, and other vital metrics
 */
export const trackPerformanceMetrics = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  try {
    // Navigation timing metrics
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    // Paint timing metrics
    const paintMetrics = performance.getEntriesByType('paint');
    const firstPaint = paintMetrics.find(metric => metric.name === 'first-paint');
    const firstContentfulPaint = paintMetrics.find(
      metric => metric.name === 'first-contentful-paint'
    );

    // Core Web Vitals
    const cls = performance
      .getEntriesByType('layout-shift')
      .reduce(
        (sum: number, entry: PerformanceEntry & { value?: number }) => sum + (entry.value || 0),
        0
      );

    const metrics = {
      pageLoadTime: navigation.loadEventEnd,
      firstPaint: firstPaint?.startTime || 0,
      firstContentfulPaint: firstContentfulPaint?.startTime || 0,
      timeToInteractive: navigation.domInteractive,
      cumulativeLayoutShift: cls,
    };

    // Log metrics in development
    if (process.env.NODE_ENV !== 'production') {
      // console.log('Performance Metrics:', metrics);
    }

    // Send metrics to analytics in production
    if (process.env.NODE_ENV === 'production') {
      // Use window.gtag if available, otherwise log to console
      if (window.gtag) {
        window.gtag('event', 'performance_metrics', metrics);
      } else {
        // console.log('Would send metrics to analytics:', metrics);
      }
    }
  } catch (error) {
    console.error('Error tracking performance metrics:', error);
  }
};

/**
 * Initialize performance monitoring
 * Sets up analytics and starts tracking performance metrics
 * Modified to prevent blocking UI rendering when performance monitoring fails
 */
export const initPerformanceMonitoring = () => {
  // Verificar si la API de performance está disponible
  if (typeof window === 'undefined' || !('performance' in window)) {
    return; // Salir silenciosamente si no está disponible
  }

  try {
    // Core Web Vitals monitoring
    const vitals: Record<string, number> = {
      cls: 0,
      inp: 0,
      lcp: 0,
    };

    // Report Web Vitals con manejo de errores
    const reportVitals = (metric: { name: string; value: number }) => {
      try {
        const name = metric.name;
        const value = metric.value;
        vitals[name.toLowerCase()] = value;

        // Send to analytics solo si gtag está disponible
        if (window.gtag) {
          window.gtag('event', 'web_vital', {
            event_category: 'Web Vitals',
            event_label: name,
            value: Math.round(value),
            non_interaction: true,
          });
        }
      } catch (error) {
        // Log errors during metric reporting without affecting app functionality.
        console.error(`Error al reportar la métrica Web Vital '${metric.name}':`, error);
      }
    };

    // Initialize Web Vitals monitoring with error handling
    import('web-vitals')
      .then(({ onCLS, onLCP, onINP }) => {
        try {
          onCLS(reportVitals);
          onLCP(reportVitals);
          onINP(reportVitals);
        } catch (vitalsError) {
          // Log errors during the initialization of Web Vitals listeners.
          console.error('Error al inicializar los listeners de Web Vitals:', vitalsError);
        }
      })
      .catch(importError => {
        // Log errors if the web-vitals module fails to load.
        console.error('Error al cargar dinámicamente el módulo web-vitals:', importError);
      });
  } catch (error) {
    // Log any other unexpected errors during performance monitoring setup.
    console.error('Error inesperado en la inicialización del monitoreo de rendimiento:', error);
  }
};
